import React, { ReactElement, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { useTheme } from '@app/utils/theme-provider';
import { AuthLayout, useEnterFrom } from './AuthLayout';
import { PasswordLoginForm } from './PasswordLoginForm';

export interface LoginPageProps {
  // Real auth wiring, owned by the app entry.
  onGoogleCredential: (credential: string) => void;
  onGoogleError: () => void;
  onLoginSuccess: () => void;
}

const Divider = ({ label }: { label: string }): ReactElement => (
  <div className="flex items-center gap-3">
    <span className="h-px flex-1 bg-border" />
    <span className="text-xs font-medium tracking-wide text-muted-foreground">{label}</span>
    <span className="h-px flex-1 bg-border" />
  </div>
);

// One unified login: email/password first, then "OR", then Google. The password is the client's way
// in; employees use Google. Both enter the app directly — there is no second factor yet, and #123
// holds the real one.
export const LoginPage = ({ onGoogleCredential, onGoogleError, onLoginSuccess }: LoginPageProps): ReactElement => {
  const { t } = useTranslation();
  const enterFrom = useEnterFrom();
  const { theme } = useTheme();
  const isDark = theme === 'system' ? window.matchMedia('(prefers-color-scheme: dark)').matches : theme === 'dark';
  // The widget takes a width in pixels, not a percentage, so it is read off the column it sits in.
  const columnRef = useRef<HTMLDivElement>(null);
  const [googleWidth, setGoogleWidth] = useState(384);
  useLayoutEffect(() => {
    const column = columnRef.current;
    const measure = (): void => {
      if (column?.offsetWidth) setGoogleWidth(Math.min(384, Math.floor(column.offsetWidth)));
    };
    measure();
    // Again whenever the column changes, as it does when a phone is turned.
    if (!column || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(column);
    return () => observer.disconnect();
  }, []);

  return (
    <AuthLayout enterFrom={enterFrom}>
      <h1 className="mb-8 text-3xl font-bold tracking-tight text-foreground">{t('login.getStarted')}</h1>
      <div ref={columnRef} className="flex flex-col gap-5">
        <PasswordLoginForm onSuccess={onLoginSuccess} />
        <Divider label={t('login.or')} />
        {/* Google's own widget rather than a button of ours: only it yields the ID token
            `authenticateWithGoogle` verifies. A custom trigger gets an OAuth access token, which that
            handler rejects. At most 384px, the form column's `max-w-sm`, inside the widget's 400px cap. */}
        <GoogleLogin
          onSuccess={credentialResponse =>
            credentialResponse.credential && onGoogleCredential(credentialResponse.credential)
          }
          onError={onGoogleError}
          theme={isDark ? 'filled_black' : 'outline'}
          text="continue_with"
          shape="rectangular"
          size="large"
          width={googleWidth}
          logo_alignment="center"
        />
      </div>
    </AuthLayout>
  );
};

export default LoginPage;
